/**
 * FahrzeugReservierungKalender — Wochen-/Monats-Kalender für Poolfahrzeug-
 * Reservierungen (Issue #164).
 *
 * Zeigt belegte Slots als farbige Balken in einem 7-Tage-Wochen-Grid.
 * Klick auf einen freien Bereich öffnet den Reservierungs-Dialog per
 * onSlotClick-Callback. Der Kalender verwendet keine externe Kalender-Library —
 * er ist mit dem Standard-Grid aufgebaut (shadcn/Tailwind), weil Drag-to-
 * create mit externen Libraries bei diesem Einsatzprofil typisch zu schwergewichtig
 * ausfällt. Vorerst: Klick auf freien Slot → Dialog, kein echtes Drag.
 */

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PoolReservierung } from './PoolFahrzeugCard';

interface FahrzeugReservierungKalenderProps {
  reservierungen: PoolReservierung[];
  fahrzeugId: string;
  onSlotClick: (beginn: Date, ende: Date) => void;
}

const HOUR_COLS = 24; // 0-23
const SLOT_HEIGHT_PX = 48; // height per hour row

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // ISO week: Mon = 1
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function formatWeekday(date: Date): string {
  return new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' }).format(date);
}

function formatHour(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}

interface ReservierungsBlock {
  id: string;
  dayIndex: number; // 0-6
  startHour: number;
  endHour: number;
  startMin: number;
  endMin: number;
  label: string | null;
}

function reservierungToBlock(res: PoolReservierung, weekStart: Date): ReservierungsBlock | null {
  if (!res.beginn || !res.ende) return null;
  const beginn = new Date(res.beginn);
  const ende = new Date(res.ende);
  const weekEnd = addDays(weekStart, 7);
  if (ende <= weekStart || beginn >= weekEnd) return null;

  const clampedBeginn = beginn < weekStart ? weekStart : beginn;
  const clampedEnde = ende > weekEnd ? weekEnd : ende;

  const dayIndex = Math.floor(
    (clampedBeginn.getTime() - weekStart.getTime()) / (24 * 60 * 60 * 1000),
  );

  return {
    id: res.id,
    dayIndex: Math.max(0, Math.min(6, dayIndex)),
    startHour: clampedBeginn.getHours(),
    startMin: clampedBeginn.getMinutes(),
    endHour: clampedEnde.getHours(),
    endMin: clampedEnde.getMinutes() === 0 ? 0 : clampedEnde.getMinutes(),
    label: res.zweck,
  };
}

export function FahrzeugReservierungKalender({
  reservierungen,
  onSlotClick,
}: FahrzeugReservierungKalenderProps) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const blocks = useMemo(
    () =>
      reservierungen
        .filter((r) => !r.storniert_am)
        .map((r) => reservierungToBlock(r, weekStart))
        .filter((b): b is ReservierungsBlock => b !== null),
    [reservierungen, weekStart],
  );

  const handleCellClick = (dayIndex: number, hour: number) => {
    const dayDate = weekDays[dayIndex];
    const beginn = new Date(dayDate);
    beginn.setHours(hour, 0, 0, 0);
    const ende = new Date(beginn);
    ende.setHours(hour + 1, 0, 0, 0);
    onSlotClick(beginn, ende);
  };

  // Visible hours: 7-19
  const visibleHours = Array.from({ length: 13 }, (_, i) => i + 7);

  return (
    <div className="w-full overflow-x-auto">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-3 px-1">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => setWeekStart((w) => addDays(w, -7))}
          aria-label="Vorherige Woche"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="text-sm font-medium text-gray-700">
          {formatWeekday(weekStart)} – {formatWeekday(addDays(weekStart, 6))}
        </span>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => setWeekStart((w) => addDays(w, 7))}
          aria-label="Nächste Woche"
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-[3rem_repeat(7,1fr)] border border-gray-200 rounded-lg overflow-hidden text-xs">
        {/* Header row */}
        <div className="bg-gray-50 border-b border-gray-200" />
        {weekDays.map((day, i) => {
          const isToday =
            day.toDateString() === new Date().toDateString();
          return (
            <div
              key={i}
              className={`border-b border-l border-gray-200 py-1.5 text-center font-medium leading-tight ${
                isToday ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-600'
              }`}
            >
              {formatWeekday(day)}
            </div>
          );
        })}

        {/* Time rows */}
        {visibleHours.map((hour) => (
          <>
            {/* Hour label */}
            <div
              key={`lbl-${hour}`}
              style={{ height: SLOT_HEIGHT_PX }}
              className="border-b border-gray-100 flex items-start justify-end pr-1.5 pt-0.5 text-gray-400 bg-white"
            >
              {formatHour(hour)}
            </div>

            {/* Day cells */}
            {weekDays.map((_, dayIndex) => {
              const block = blocks.find(
                (b) =>
                  b.dayIndex === dayIndex &&
                  hour >= b.startHour &&
                  hour < (b.endMin > 0 ? b.endHour + 1 : b.endHour),
              );
              const isBooked = !!block;

              return (
                <div
                  key={`cell-${hour}-${dayIndex}`}
                  style={{ height: SLOT_HEIGHT_PX }}
                  className={`border-b border-l border-gray-100 cursor-pointer transition-colors relative ${
                    isBooked
                      ? 'bg-red-100'
                      : 'hover:bg-blue-50 active:bg-blue-100'
                  }`}
                  onClick={() => !isBooked && handleCellClick(dayIndex, hour)}
                  title={isBooked ? (block.label ?? 'Reserviert') : `Reservieren ab ${formatHour(hour)}`}
                >
                  {/* Label only on first hour of block */}
                  {block && hour === block.startHour && (
                    <span className="absolute inset-x-0.5 top-0.5 text-[10px] text-red-700 font-medium truncate px-1 leading-tight">
                      {block.label ?? 'Reserviert'}
                    </span>
                  )}
                </div>
              );
            })}
          </>
        ))}
      </div>
    </div>
  );
}
